/**
 * Dashboard Service — Centralized API client for the Business Dashboard.
 * All calls hit the BNova backend (PostgreSQL/Prisma + Qdrant via ai-services).
 */

import axios from "axios";
import API_BASE_URL from "../config/api";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

// Attach JWT to every request
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("admin_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ─── Platform Overview ───────────────────────────────────────────────────────

/**
 * Fetches the full platform overview (users, content, engagement, features).
 * @returns {Promise<Object>} Overview data
 */
export const getOverview = async () => {
  const { data } = await api.get("/business-dashboard/overview");
  return data.data;
};

/**
 * Fetches time-series trend data (signups, queries, documents).
 * @param {number} days — Lookback window (7–90)
 * @returns {Promise<Object>} Trend data with labels and datasets
 */
export const getTrends = async (days = 30) => {
  const { data } = await api.get(`/business-dashboard/trends?days=${days}`);
  return data.data;
};

export const getTokenAnalytics = async (days = 30) => {
  const { data } = await api.get(`/business-dashboard/token-analytics?days=${days}`);
  return data.data;
};

export const getUserTokenUsage = async ({ username, days = 30 } = {}) => {
  const params = new URLSearchParams({ days: String(days) });
  if (username) params.append("username", username);
  const { data } = await api.get(`/business-dashboard/user-token-usage?${params.toString()}`);
  return data.data;
};

export const getLocationAnalytics = async (timeframe = "1M", country = "Worldwide") => {
  const params = new URLSearchParams({ timeframe });
  if (country && country !== "Worldwide") {
    params.append("country", country);
  }
  const { data } = await api.get(`/user-analytics/location-data?${params.toString()}`);
  return data;
};

// ─── Vector / Qdrant ─────────────────────────────────────────────────────────

/**
 * Health check for the vector database.
 * @returns {Promise<Object>} Health status
 */
export const getVectorHealth = async () => {
  const { data } = await api.get("/vector/health");
  return data;
};

/**
 * Semantic search against the Qdrant vector store.
 * @param {string} query — Natural-language query
 * @param {number} topK — Number of results
 * @returns {Promise<Array>} Search results
 */
export const searchVector = async (query, topK = 5) => {
  const { data } = await api.post("/vector/search", { query, top_k: topK });
  return data.results || data;
};

// ─── User Analytics ──────────────────────────────────────────────────────────

/**
 * User analytics stats (thin wrapper around overview for backward compat).
 */
export const getUserStats = async () => {
  const overview = await getOverview();
  return {
    totalUsers: overview.users?.total || 0,
    activeUsers: overview.users?.active || 0,
    inactiveUsers: overview.users?.inactive || 0,
    totalInvitedUsers: (overview.waitlist?.pending || 0) + (overview.waitlist?.approved || 0),
  };
};

export default {
  getOverview,
  getTrends,
  getVectorHealth,
  searchVector,
  getUserStats,
  getTokenAnalytics,
  getUserTokenUsage,
  getLocationAnalytics,
};
